import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import LegalDisclaimers from '../components/LegalDisclaimers';
import FeedbackModal from '../components/FeedbackModal';
import ProductAnalysisWithViews from '../components/ProductAnalysisWithViews';
import { api, ProductWithComponents, ComponentSummary } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';

// Component interface is now imported from API service

const ProductAnalysis = () => {
  const { productSlug } = useParams<{ productSlug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductWithComponents | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'correction' | 'general_feedback'>('general_feedback');
  const [showAllComponents, setShowAllComponents] = useState(false);
  const [componentViewMode, setComponentViewMode] = useState<'cards' | 'breakdown'>('cards');

  // Load product data
  const loadProduct = async (showRefreshToast = false) => {
    if (!productSlug) {
      setError('Product not found');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const productData = await api.getProductBySlug(productSlug, 'all');
      setProduct(productData);
      if (showRefreshToast) {
        toast.success('Product data refreshed');
      }
    } catch (err) {
      console.error('Failed to load product:', err);
      setError('Failed to load product data');
      if (!showRefreshToast) {
        toast.error('Failed to load product data');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadProduct();
  }, [productSlug]);

  // Auto-reload while analysis is in progress
  useEffect(() => {
    if (!product) return;

    const isAnalyzing = product.analysis_status === 'analyzing' || product.analysis_progress < 100;
    
    if (isAnalyzing) {
      const interval = setInterval(() => {
        loadProduct(false); // Don't show toast for auto-refresh
      }, 5000); // Refresh every 5 seconds

      return () => clearInterval(interval);
    }
  }, [product?.analysis_status, product?.analysis_progress]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadProduct(true);
  };



  const getStatusText = (component: ComponentSummary) => {
    switch (component.analysis_status) {
      case 'completed':
        return `✅ Analysis complete`;
      case 'analyzing':
        return '🔄 Analyzing...';
      case 'pending':
        return `⏳ Queued`;
      default:
        return '';
    }
  };



  // Loading state
  if (isLoading && !isRefreshing) {
    return (
      <>
        <title>Loading Product Analysis - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-600">Loading product analysis...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <>
        <title>Product Not Found - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📱</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h1>
              <p className="text-gray-600 mb-6">{error || 'The product you\'re looking for doesn\'t exist.'}</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Back to Home
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {isRefreshing ? (
                    <>
                      <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{product.name} Analysis - ProductTrace</title>
      <meta name="description" content={`Detailed component breakdown and analysis of ${product.name}`} />

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button and Actions */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to ProductTrace
            </button>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setFeedbackType('correction');
                  setShowFeedbackModal(true);
                }}
                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                Submit Correction
              </button>
              
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {isRefreshing ? (
                  <>
                    <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-2" />
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Product Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-gray-900">
                📱 {product.name} Analysis
              </h1>
              <div className="flex items-center space-x-2 text-sm">
                {product.analysis_status === 'completed' && product.analysis_progress >= 100 ? (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-600 font-medium">Analysis Complete</span>
                  </>
                ) : product.analysis_status === 'analyzing' ? (
                  <>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-blue-600 font-medium">Analyzing...</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-500">Live Data</span>
                  </>
                )}
              </div>
            </div>
            
            <p className="text-lg text-gray-600 mb-4">
              📱 {product.name} - {formatCurrency(product.retail_price)} retail | {formatCurrency(product.manufacturing_cost)} estimated manufacturing
            </p>
            
            {product.description && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Product Description</h3>
                <p className="text-sm text-blue-800">{product.description}</p>
              </div>
            )}

            {/* Analysis Progress - Only show if analysis is not complete */}
            {(product.analysis_status === 'analyzing' || product.analysis_progress < 100) && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700">Analysis Progress</span>
                  <span className="text-gray-600">{product.analysis_progress}% complete</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${product.analysis_progress}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>📊 Coverage: {product.cost_coverage_percentage}% ✓</span>
                  <span>🔍 Confidence: {product.confidence_score || 'N/A'}%</span>
                  <span>📈 {product.analyzed_components}/{product.total_components} components</span>
                </div>
                <div className="mt-3 text-xs text-blue-600 flex items-center">
                  <div className="animate-spin w-3 h-3 mr-2 border border-blue-600 border-t-transparent rounded-full"></div>
                  Auto-refreshing every 5 seconds...
                </div>
              </div>
            )}
          </div>

          {/* Impact Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Environmental Impact */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 text-xl">🌱</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Environmental Impact</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Carbon Footprint</span>
                  <span className="text-sm font-medium text-gray-900">
                    {product.overall_environmental_impact?.carbon_footprint_kg 
                      ? `${product.overall_environmental_impact.carbon_footprint_kg} kg CO₂` 
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Energy Use</span>
                  <span className="text-sm font-medium text-gray-900">
                    {product.overall_environmental_impact?.energy_consumption_kwh 
                      ? `${product.overall_environmental_impact.energy_consumption_kwh} kWh` 
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Water Usage</span>
                  <span className="text-sm font-medium text-gray-900">
                    {product.overall_environmental_impact?.water_usage_liters 
                      ? `${product.overall_environmental_impact.water_usage_liters} L` 
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Recyclability</span>
                  <span className="text-sm font-medium text-green-600">
                    {product.overall_environmental_impact?.recyclability_percentage 
                      ? `${product.overall_environmental_impact.recyclability_percentage}% recyclable` 
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Labor & Social */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-blue-600 text-xl">👥</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Labor & Social</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Workers Involved</span>
                  <span className="text-sm font-medium text-gray-900">
                    {product.total_worker_count 
                      ? `${product.total_worker_count.toLocaleString()} people` 
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Labor Summary</span>
                  <span className="text-sm font-medium text-gray-900">
                    {product.overall_labor_summary 
                      ? product.overall_labor_summary.substring(0, 20) + '...' 
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Production Countries</span>
                  <span className="text-sm font-medium text-gray-900">
                    {product.primary_production_countries?.length || 0} countries
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Risk Score</span>
                  <span className={`text-sm font-medium ${
                    product.overall_risk_score && product.overall_risk_score >= 70 ? 'text-red-600' :
                    product.overall_risk_score && product.overall_risk_score >= 40 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {product.overall_risk_score ? `${product.overall_risk_score}/100` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Geographic Origin */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-purple-600 text-xl">🌍</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">Component Origins</h3>
                  <p className="text-sm text-gray-600">Where individual components are manufactured (by cost value)</p>
                </div>
              </div>
              <div className="space-y-3">
                {product.primary_production_countries && product.primary_production_countries.length > 0 ? (
                  <>
                    {product.primary_production_countries.slice(0, 4).map((country, index) => {
                      const getCountryFlag = (countryName: string) => {
                        const flags: { [key: string]: string } = {
                          'China': '🇨🇳', 'Taiwan': '🇹🇼', 'South Korea': '🇰🇷', 'Japan': '🇯🇵',
                          'United States': '🇺🇸', 'USA': '🇺🇸', 'Germany': '🇩🇪', 'Vietnam': '🇻🇳',
                          'Thailand': '🇹🇭', 'Malaysia': '🇲🇾', 'Singapore': '🇸🇬', 'India': '🇮🇳'
                        };
                        return flags[countryName] || '🌍';
                      };
                      
                      return (
                        <div key={index} className="flex justify-between">
                          <span className="text-sm text-gray-600">
                            {getCountryFlag(country.country)} {country.country}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{country.percentage}%</span>
                        </div>
                      );
                    })}
                    
                    {/* Show total percentage with clear explanation */}
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700 font-medium">Coverage</span>
                        <span className="text-gray-900 font-medium">
                          {product.primary_production_countries.slice(0, 4).reduce((sum, c) => sum + c.percentage, 0).toFixed(1)}% of {product.cost_coverage_percentage}%
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {product.primary_production_countries.slice(0, 4).reduce((sum, c) => sum + c.percentage, 0) < 100 ? (
                          <>Percentages reflect analyzed components only. Some components may lack location data.</>
                        ) : (
                          <>Based on component manufacturing cost analysis.</>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <div className="text-sm text-gray-500">No component origin data available</div>
                    <div className="text-xs text-gray-400 mt-1">Component analysis may still be in progress</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Consolidated Components Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                🔧 Product Components ({product.components.length} total • {product.cost_coverage_percentage}% cost coverage)
              </h2>
              <div className="flex items-center space-x-3">
                {/* View Mode Toggle */}
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setComponentViewMode('cards')}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      componentViewMode === 'cards' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📋 Cards
                  </button>
                  <button
                    onClick={() => setComponentViewMode('breakdown')}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      componentViewMode === 'breakdown' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    💰 Cost Breakdown
                  </button>
                </div>
                
                {/* Show All Toggle */}
                {product.components.length > 6 && (
                  <button
                    onClick={() => setShowAllComponents(!showAllComponents)}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {showAllComponents ? 'Show Main Components' : `Show All ${product.components.length} Components`}
                  </button>
                )}
              </div>
            </div>

            {/* Cost Breakdown View */}
            {componentViewMode === 'breakdown' && (
              <div className="space-y-3">
                {(showAllComponents ? product.components : product.components.slice(0, 6)).map((component) => (
                  <div 
                    key={component.id} 
                    className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    onClick={() => navigate(`/products/${productSlug}/components/${component.slug}`)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
                            {component.name}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            component.tier === 'major' ? 'bg-red-100 text-red-800' :
                            component.tier === 'secondary' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {component.tier}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {component.category || 'Unknown'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-600 font-medium">{formatCurrency(component.cost)}</span>
                          <span className="text-sm text-gray-500">({component.cost_percentage || 0}%)</span>
                          <span className="text-xs text-gray-400">→</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${component.cost_percentage || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cards View */}
            {componentViewMode === 'cards' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(showAllComponents ? product.components : product.components.slice(0, 6)).map((component) => {
                  const isMajorComponent = !showAllComponents || component.tier === 'major' || product.components.indexOf(component) < 6;
                  
                  return (
                    <div
                      key={component.id}
                      className={`p-4 border rounded-lg transition-all cursor-pointer ${
                        isMajorComponent 
                          ? 'border-gray-200 hover:shadow-md hover:border-blue-300 bg-white' 
                          : 'border-gray-100 hover:shadow-sm hover:border-gray-300 bg-gray-50'
                      }`}
                      onClick={() => navigate(`/products/${productSlug}/components/${component.slug}`)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className={`font-medium ${isMajorComponent ? 'text-gray-900' : 'text-gray-700'}`}>
                          {component.name}
                        </h3>
                        <span className={`px-2 py-1 rounded text-xs ${
                          component.tier === 'major' ? 'bg-red-100 text-red-800' :
                          component.tier === 'secondary' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {component.tier}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center mb-3">
                        <span className={`text-lg font-bold ${isMajorComponent ? 'text-gray-900' : 'text-gray-700'}`}>
                          {formatCurrency(component.cost)}
                        </span>
                        <span className={`text-sm ${isMajorComponent ? 'text-gray-600' : 'text-gray-500'}`}>
                          {component.cost_percentage ? `${component.cost_percentage}%` : 'N/A'}
                        </span>
                      </div>
                      
                      {/* Component Details */}
                      <div className="space-y-2 text-xs text-gray-600">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center">
                            <span className="mr-1">🌍</span>
                            {component.category || 'Unknown'}
                          </span>
                          <span className={`${isMajorComponent ? 'text-gray-500' : 'text-gray-400'}`}>
                            Click to explore
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center">
                            <span className="mr-1">📊</span>
                            {getStatusText(component)}
                          </span>
                          <span className="flex items-center">
                            <span className="mr-1">💰</span>
                            {formatCurrency(component.cost)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Component Summary */}
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Showing {showAllComponents ? product.components.length : Math.min(6, product.components.length)} of {product.components.length} components
                  {componentViewMode === 'breakdown' && ` • ${componentViewMode === 'breakdown' ? 'Cost breakdown' : 'Card'} view`}
                </span>
                <span>
                  {product.components.filter(c => c.tier === 'major').length} major • {' '}
                  {product.components.filter(c => c.tier === 'secondary').length} secondary • {' '}
                  {product.components.filter(c => c.tier === 'minor').length} minor
                </span>
              </div>
            </div>
          </div>

          {/* Product Summary */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              📋 Product Summary
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Details</h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>Category:</span>
                    <span className="font-medium">{product.category || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Brand:</span>
                    <span className="font-medium">{product.brand || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Model:</span>
                    <span className="font-medium">{product.model || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Analysis Status:</span>
                    <span className={`font-medium ${
                      product.analysis_status === 'completed' ? 'text-green-600' :
                      product.analysis_status === 'analyzing' ? 'text-blue-600' : 'text-gray-600'
                    }`}>
                      {product.analysis_status}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Analysis</h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>Retail Price:</span>
                    <span className="font-medium">${product.retail_price || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Manufacturing Cost:</span>
                    <span className="font-medium">${product.manufacturing_cost || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cost Coverage:</span>
                    <span className="font-medium">{product.cost_coverage_percentage}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Components Analyzed:</span>
                    <span className="font-medium">{product.analyzed_components}/{product.total_components}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Supply Chain Deep Dive */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              🌐 Global Supply Chain Analysis
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Production Concentration</h3>
                <div className="space-y-4">
                  {product.supply_chain_concentration && product.supply_chain_concentration.length > 0 ? (
                    product.supply_chain_concentration.map((location, index) => {
                      const getCountryFlag = (country: string) => {
                        const flags: { [key: string]: string } = {
                          'China': '🇨🇳',
                          'Taiwan': '🇹🇼',
                          'South Korea': '🇰🇷',
                          'Japan': '🇯🇵',
                          'United States': '🇺🇸',
                          'USA': '🇺🇸',
                          'Germany': '🇩🇪',
                          'Vietnam': '🇻🇳',
                          'Thailand': '🇹🇭',
                          'Malaysia': '🇲🇾',
                          'Singapore': '🇸🇬',
                          'India': '🇮🇳'
                        };
                        return flags[country] || '🌍';
                      };
                      
                      return (
                        <div key={index}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-900">
                              {getCountryFlag(location.country)} {location.country} ({location.percentage}%)
                            </span>
                            <span className="text-sm text-gray-600">${location.value_usd} value</span>
                          </div>
                          <div className="text-sm text-gray-600 mb-2">
                            <strong>Why:</strong> {location.why_concentrated}
                          </div>
                          <div className="text-xs text-gray-500">
                            <strong>Labor:</strong> {location.labor_summary}
                          </div>
                          <div className="text-xs text-gray-500">
                            <strong>Environmental:</strong> {location.environmental_summary}
                          </div>
                          {location.risk_factors && location.risk_factors.length > 0 && (
                            <div className="text-xs text-gray-500">
                              <strong>Risks:</strong> {location.risk_factors.join(', ')}
                            </div>
                          )}
                          {location.major_suppliers && location.major_suppliers.length > 0 && (
                            <div className="text-xs text-gray-500">
                              <strong>Major suppliers:</strong> {location.major_suppliers.join(', ')}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : product.primary_production_countries && product.primary_production_countries.length > 0 ? (
                    // Fallback to primary production countries if supply chain concentration not available
                    product.primary_production_countries.map((country, index) => {
                      const getCountryFlag = (countryName: string) => {
                        const flags: { [key: string]: string } = {
                          'China': '🇨🇳',
                          'Taiwan': '🇹🇼',
                          'South Korea': '🇰🇷',
                          'Japan': '🇯🇵',
                          'United States': '🇺🇸',
                          'USA': '🇺🇸',
                          'Germany': '🇩🇪',
                          'Vietnam': '🇻🇳',
                          'Thailand': '🇹🇭',
                          'Malaysia': '🇲🇾',
                          'Singapore': '🇸🇬',
                          'India': '🇮🇳'
                        };
                        return flags[countryName] || '🌍';
                      };
                      
                      return (
                        <div key={index}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-900">
                              {getCountryFlag(country.country)} {country.country} ({country.percentage}%)
                            </span>
                            <span className="text-sm text-gray-600">
                              ${((product.manufacturing_cost || 0) * country.percentage / 100).toFixed(0)} value
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            Production location based on component analysis
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-4xl mb-2">🌐</div>
                      <div>No supply chain concentration data available</div>
                      <div className="text-sm">Product analysis may still be in progress</div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h3>
                <div className="space-y-3">
                  {product.risk_assessment ? (
                    <>
                      {product.risk_assessment.geopolitical_risk && (
                        <div className={`flex items-center justify-between p-3 rounded-lg ${
                          product.risk_assessment.geopolitical_risk === 'high' ? 'bg-red-50' :
                          product.risk_assessment.geopolitical_risk === 'medium' ? 'bg-yellow-50' : 'bg-green-50'
                        }`}>
                          <div>
                            <div className="font-medium text-gray-900">Geopolitical Risk</div>
                            <div className="text-sm text-gray-600">
                              {product.risk_assessment.geopolitical_risk_description || 'Trade tensions, export controls'}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            product.risk_assessment.geopolitical_risk === 'high' ? 'bg-red-200 text-red-800' :
                            product.risk_assessment.geopolitical_risk === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'
                          }`}>
                            {product.risk_assessment.geopolitical_risk.charAt(0).toUpperCase() + product.risk_assessment.geopolitical_risk.slice(1)}
                          </span>
                        </div>
                      )}
                      
                      {product.risk_assessment.supply_stability_risk && (
                        <div className={`flex items-center justify-between p-3 rounded-lg ${
                          product.risk_assessment.supply_stability_risk === 'high' ? 'bg-red-50' :
                          product.risk_assessment.supply_stability_risk === 'medium' ? 'bg-yellow-50' : 'bg-green-50'
                        }`}>
                          <div>
                            <div className="font-medium text-gray-900">Supply Stability</div>
                            <div className="text-sm text-gray-600">
                              {product.risk_assessment.supply_stability_description || 'Established suppliers, backup options'}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            product.risk_assessment.supply_stability_risk === 'high' ? 'bg-red-200 text-red-800' :
                            product.risk_assessment.supply_stability_risk === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'
                          }`}>
                            {product.risk_assessment.supply_stability_risk.charAt(0).toUpperCase() + product.risk_assessment.supply_stability_risk.slice(1)}
                          </span>
                        </div>
                      )}
                      
                      {product.risk_assessment.environmental_risk && (
                        <div className={`flex items-center justify-between p-3 rounded-lg ${
                          product.risk_assessment.environmental_risk === 'high' ? 'bg-red-50' :
                          product.risk_assessment.environmental_risk === 'medium' ? 'bg-yellow-50' : 'bg-green-50'
                        }`}>
                          <div>
                            <div className="font-medium text-gray-900">Environmental Risk</div>
                            <div className="text-sm text-gray-600">
                              {product.risk_assessment.environmental_risk_description || 'High energy use, limited recycling'}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            product.risk_assessment.environmental_risk === 'high' ? 'bg-red-200 text-red-800' :
                            product.risk_assessment.environmental_risk === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'
                          }`}>
                            {product.risk_assessment.environmental_risk.charAt(0).toUpperCase() + product.risk_assessment.environmental_risk.slice(1)}
                          </span>
                        </div>
                      )}
                      
                      {product.risk_assessment.labor_risk && (
                        <div className={`flex items-center justify-between p-3 rounded-lg ${
                          product.risk_assessment.labor_risk === 'high' ? 'bg-red-50' :
                          product.risk_assessment.labor_risk === 'medium' ? 'bg-yellow-50' : 'bg-green-50'
                        }`}>
                          <div>
                            <div className="font-medium text-gray-900">Labor Risk</div>
                            <div className="text-sm text-gray-600">
                              {product.risk_assessment.labor_risk_description || 'Working conditions and compliance'}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            product.risk_assessment.labor_risk === 'high' ? 'bg-red-200 text-red-800' :
                            product.risk_assessment.labor_risk === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'
                          }`}>
                            {product.risk_assessment.labor_risk.charAt(0).toUpperCase() + product.risk_assessment.labor_risk.slice(1)}
                          </span>
                        </div>
                      )}
                      
                      {product.risk_assessment.regulatory_risk && (
                        <div className={`flex items-center justify-between p-3 rounded-lg ${
                          product.risk_assessment.regulatory_risk === 'high' ? 'bg-red-50' :
                          product.risk_assessment.regulatory_risk === 'medium' ? 'bg-yellow-50' : 'bg-green-50'
                        }`}>
                          <div>
                            <div className="font-medium text-gray-900">Regulatory Risk</div>
                            <div className="text-sm text-gray-600">
                              {product.risk_assessment.regulatory_risk_description || 'Changing standards and compliance'}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            product.risk_assessment.regulatory_risk === 'high' ? 'bg-red-200 text-red-800' :
                            product.risk_assessment.regulatory_risk === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'
                          }`}>
                            {product.risk_assessment.regulatory_risk.charAt(0).toUpperCase() + product.risk_assessment.regulatory_risk.slice(1)}
                          </span>
                        </div>
                      )}
                      
                      {product.overall_risk_score && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-900">Overall Risk Score</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              product.overall_risk_score >= 70 ? 'bg-red-200 text-red-800' :
                              product.overall_risk_score >= 40 ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'
                            }`}>
                              {product.overall_risk_score}/100
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-4xl mb-2">⚠️</div>
                      <div>No risk assessment data available</div>
                      <div className="text-sm">Risk analysis may still be in progress</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* New Multi-View Analysis Section */}
          <ProductAnalysisWithViews 
            product={product}
          />

          {/* Legal Disclaimers */}
          <LegalDisclaimers />
        </div>

        {/* Feedback Modal */}
        {showFeedbackModal && (
          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
            initialType={feedbackType}
            productId={product?.id}
            productName={product?.name}
          />
        )}
      </div>
    </>
  );
};

export default ProductAnalysis; 